export interface ListingCard {
  id: string;
  publicName: string;
  shopName: string;
  area: string | null;
  declaredAge: number | null;
  newcomerLabel: string | null;
  firstDay?: string | null;
  firstDayLabel?: string | null;
  industryExperience?: "experienced" | "inexperienced" | "unknown";
  experienceEvidence?: string | null;
  scheduleText: string | null;
  isWorkingNow: boolean;
  photoUrl: string | null;
  profileUrl: string;
  observedAt: string;
  innocenceLevel?: "strong" | "medium" | null;
  innocenceLabel?: string | null;
  innocenceReasons?: string[];
}

export interface ShopReviewCard {
  storeName: string;
  officialUrl: string | null;
  summary: string;
  url: string;
  kind: "forum";
}

export interface SearchResult {
  summary: string;
  listings: ListingCard[];
  reviews: ShopReviewCard[];
}

function parseSseBlock(block: string): { event: string; data: unknown } | null {
  let event = "message";
  let data = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!data) return null;
  try {
    return { event, data: JSON.parse(data) as unknown };
  } catch {
    return null;
  }
}

function isListingCard(value: unknown): value is ListingCard {
  if (typeof value !== "object" || value === null) return false;
  const card = value as Partial<ListingCard>;
  return (
    typeof card.id === "string" &&
    typeof card.publicName === "string" &&
    typeof card.shopName === "string" &&
    typeof card.profileUrl === "string"
  );
}

function isReviewCard(value: unknown): value is ShopReviewCard {
  if (typeof value !== "object" || value === null) return false;
  const card = value as Partial<ShopReviewCard>;
  return typeof card.summary === "string" && typeof card.url === "string";
}

export interface AppConfig {
  turnstileSiteKey: string | null;
  unlocked: boolean;
}

export async function loadConfig(): Promise<AppConfig> {
  const response = await fetch("/api/config", { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error("設定を読み取れませんでした。");
  }
  const config = (await response.json()) as Partial<AppConfig>;
  return {
    turnstileSiteKey: typeof config.turnstileSiteKey === "string" ? config.turnstileSiteKey : null,
    unlocked: config.unlocked === true,
  };
}

export async function unlockWithPassphrase(passphrase: string): Promise<void> {
  const response = await fetch("/api/unlock", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ passphrase }),
  });
  if (response.status === 403) {
    throw new Error("合言葉が違います。");
  }
  if (!response.ok) {
    throw new Error("入れませんでした。");
  }
}

export async function searchListings(input: {
  conversationId: string;
  message: string;
  newcomerOnly: boolean;
  inexperiencedOnly: boolean;
  innocentOnly: boolean;
  turnstileToken?: string;
  onStatus?: (text: string) => void;
}): Promise<SearchResult> {
  const response = await fetch("/api/chat", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      conversationId: input.conversationId,
      message: input.message,
      adultConfirmed: true,
      newcomerOnly: input.newcomerOnly,
      inexperiencedOnly: input.inexperiencedOnly,
      innocentOnly: input.innocentOnly,
      ...(input.turnstileToken ? { turnstileToken: input.turnstileToken } : {}),
    }),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(error.error ?? `検索に失敗しました（${String(response.status)}）`);
  }
  if (!response.body) {
    throw new Error("応答を読み取れませんでした。");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let summary = "";
  let listings: ListingCard[] = [];
  let reviews: ShopReviewCard[] = [];

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";
    for (const block of blocks) {
      const parsed = parseSseBlock(block);
      if (!parsed) continue;
      if (parsed.event === "delta" && typeof parsed.data === "object" && parsed.data !== null) {
        const text = (parsed.data as { text?: unknown }).text;
        if (typeof text === "string") {
          summary = text;
          input.onStatus?.(text);
        }
      }
      if (parsed.event === "listings" && Array.isArray(parsed.data)) {
        listings = parsed.data.filter(isListingCard);
      }
      if (parsed.event === "reviews" && Array.isArray(parsed.data)) {
        reviews = parsed.data.filter(isReviewCard);
      }
    }
    if (done) break;
  }

  return { summary, listings, reviews };
}
